import { z } from 'zod';

export enum NotificationStatusEnum {
	PENDING = 'PENDENTE',
	ACKNOWLEDGED = 'ANALISADO',
}

export enum ChangeTypeEnum {
	MAPPING = 'Alteração de mapeamento',
	FIRED = 'Status alterado para "Demitido"',
	ADDRESS = 'Alteração de endereço',
	WORKSTATION = 'Alteração de local de trabalho',
	POSITION = 'Alteração no Organograma',
	VACATION = 'Alteração de férias',
	ABSENCE = 'Colaborador em licença',
	SITUATION = 'Alteração de situação',
	SHIFT = 'Alteração de turno',
	EXTRA_TRIP = 'Ida extra no dia de treinamento',
}

export const MappingDataSchema = z.object({
	checked: z.boolean(),
	trainingName: z.string().optional(),
});

export const FiredDataSchema = z.object({
	status: z.string(),
});

export const AddressDataSchema = z.object({
	address: z.string(),
});

export const WorkStationDataSchema = z.object({
	workStationId: z.number(),
	workStationName: z.string(),
});

export const PositionChangeSchema = z.object({
	positionId: z.union([z.number(), z.null()]),
	generalInformationJobTitleId: z.union([z.number(), z.null()]),
	generalInformationJobTitleName: z.union([z.string(), z.null()]),
	coordinationId: z.union([z.number(), z.null()]),
	coordinationName: z.union([z.string(), z.null()]),
	workStationId: z.union([z.number(), z.null()]),
	workStationName: z.union([z.string(), z.null()]),
	managementName: z.union([z.string(), z.null()]),
	companyName: z.union([z.string(), z.null()]),
	boardName: z.union([z.string(), z.null()]),
	poleName: z.union([z.string(), z.null()]),
});

export const VacationDataSchema = z.object({
	employeeId: z.number(),
	vacationPeriods: z.array(
		z.object({
			endDate: z.string(),
			startDate: z.string(),
			salaryAdvance: z.boolean(),
			vacationCovers: z.array(z.unknown()),
			vacationCoversWorkDays: z.array(z.array(z.unknown())),
		}),
	),
});

export const AbsenceDataSchema = z.object({
	employeeId: z.number().optional(),
	type: z.string().optional(),
	specification: z.string().optional(),
	startDate: z.string().nullable().optional(),
	endDate: z.string().nullable().optional(),
	absencePeriods: z
		.array(
			z.object({
				startDate: z.string().nullable(),
				endDate: z.string().nullable(),
			}),
		)
		.optional(),
});

export const SituationDataSchema = z.object({
	situationData: z.object({
		trainingDays: z.record(
			z.object({
				value: z.string(),
			}),
		),
	}),
});

export const ShiftDataSchema = z.object({
	shiftData: z.object({
		trainingDays: z.record(
			z.object({
				timelineValue: z.string(),
				teamStructureShift: z.string(),
			}),
		),
	}),
});

export const ModalDataSchema = z.union([
	MappingDataSchema,
	FiredDataSchema,
	AddressDataSchema,
	WorkStationDataSchema,
	PositionChangeSchema,
	VacationDataSchema,
	AbsenceDataSchema,
]);

export const EmployeeChangesDataSchema = z.union([
	ModalDataSchema,
	SituationDataSchema,
	ShiftDataSchema,
]);

export const EmployeesChangesSchema = z.object({
	id: z.number(),
	status: z.enum([
		NotificationStatusEnum.PENDING,
		NotificationStatusEnum.ACKNOWLEDGED,
	]),
	modificationTimestamp: z.string(),
	employeeName: z.string(),
	employeeNumber: z.string(),
	employeeId: z.number(),
	training: z.string(),
	trainingFileId: z.number(),
	trainingPlanningId: z.number(),
	trainingPlanningDate: z.union([z.array(z.string()), z.null()]),
	necessaryTraining: z.string(),
	changeType: z.enum([
		ChangeTypeEnum.ADDRESS,
		ChangeTypeEnum.FIRED,
		ChangeTypeEnum.MAPPING,
		ChangeTypeEnum.WORKSTATION,
		ChangeTypeEnum.POSITION,
		ChangeTypeEnum.VACATION,
		ChangeTypeEnum.ABSENCE,
		ChangeTypeEnum.SITUATION,
		ChangeTypeEnum.SHIFT,
		ChangeTypeEnum.EXTRA_TRIP,
	]),
	beforeChange: EmployeeChangesDataSchema.nullable(),
	afterChange: EmployeeChangesDataSchema.nullable(),
	acknowledged: z.boolean(),
	acknowledgedBy: z.union([z.string(), z.null()]),
	acknowledgedAt: z.union([z.string(), z.null()]),
});

export type EmployeesChanges = z.infer<typeof EmployeesChangesSchema>;

const FilterOption = z
	.object({
		id: z
			.union([z.number(), z.string()])
			.transform((id) => String(id))
			.pipe(z.string()),
		name: z.string(),
	})
	.transform(({ id, name }) => ({
		value: id,
		label: name,
	}));

const FilterSingleOption = z.string().transform((value) => {
	return {
		value,
		label: value,
	};
});

export const EmployeeChangesFilterOptionsSchema = z.object({
	status: z.array(FilterSingleOption).default([]),
	employee: z.array(FilterOption).default([]),
	trainings: z.array(FilterOption).default([]),
	types: z.array(FilterSingleOption).default([]),
});
